import { TMkAuthenticationState } from '../types'

export type LockoutVariant = 'PASSWORD_TEMPORARY' | 'MFA_CRITICAL' | 'NONE'

/**
 * Determines the type of account lockout based on authentication state
 * @param authState - The current authentication state
 * @returns The lockout variant or 'NONE' if not locked
 */
export const getLockoutVariant = (authState: TMkAuthenticationState): LockoutVariant => {
  // Check if account is currently locked
  if (!authState.isAccountLocked && !authState.isMfaLocked) {
    return 'NONE'
  }

  // MFA lockout takes precedence - this is critical and requires phone unlock
  if (authState.isMfaLocked) {
    return 'MFA_CRITICAL'
  }

  // Password lockout - temporary with timer
  if (authState.isAccountLocked) {
    return 'PASSWORD_TEMPORARY'
  }

  return 'NONE'
}

/**
 * Checks if a username is currently locked (either password or MFA)
 * @param authState - The current authentication state
 * @param username - The username to check
 * @returns True if the username is currently locked
 */
export const isUsernameLocked = (authState: TMkAuthenticationState, username: string): boolean => {
  const variant = getLockoutVariant(authState)
  if (variant === 'NONE') {
    return false
  }

  return authState.lockedUsername === username
}

/**
 * Checks if a password lockout is still active (within time limit)
 * @param authState - The current authentication state
 * @returns True if password lockout is active and hasn't expired
 */
export const isPasswordLockoutActive = (authState: TMkAuthenticationState): boolean => {
  if (!authState.isAccountLocked || !authState.lockoutEndTime) {
    return false
  }

  return Date.now() < authState.lockoutEndTime
}

/**
 * Calculates remaining lockout time in seconds
 * @param authState - The current authentication state
 * @returns Remaining time in seconds, or 0 if not locked or expired
 */
export const getRemainingLockoutTime = (authState: TMkAuthenticationState): number => {
  if (!authState.lockoutEndTime) {
    return 0
  }

  const remaining = Math.max(0, Math.floor((authState.lockoutEndTime - Date.now()) / 1000))
  return remaining
}
